import React, { useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Plus, TrashIcon, ArrowLeft, Send, Loader2 } from "lucide-react"
import moment from "moment"
import { useToast } from "~/utils/Toast"
import { formatCurrency } from "~/utils/helpers"
import Booking from "~/types/Booking"
import Payment from "~/types/Payment"
import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"
import CatchTable from "~/library/CatchTable"
import CatchModal from "~/library/CatchModal"
import ResourceNotes from "~/library/ResourceNotes"
import ConfirmationModal from "~/library/ConfirmationModal"

import PaymentModal from "./PaymentModal"
import LineItemModal from "./LineItemModal"
import BookingStatusManager from "~/components/BookingStatusManager"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiBookings, apiPayments } from "~/routes"
import NavigationLink from "~/library/NavigationLink"
import {
  getBookingsBackPath,
  getBookingsBackTitle,
  getBookingsNavigationState,
} from "~/utils/navigationState"
import Pack from "~/types/Pack"
import PackItem from "~/types/PackItem"
import BookingItem from "~/types/BookingItem"

const BookingView = () => {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false)
  const [deletingPayment, setDeletingPayment] = useState<string | null>(null)
  const [sendingInvoice, setSendingInvoice] = useState<string | null>(null)
  const [confirmingInvoice, setConfirmingInvoice] = useState<string | null>(null)
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)

  const { venueSlug } = getAppConfig()
  const navigate = useNavigate()
  const { id } = useParams()
  const endpoint = apiBookings.show.path({ venueSlug, id })
  const {
    data: booking,
    refetch,
    isLoading,
  } = useFetchResource<{ booking: Booking }, "booking">(endpoint, "booking", {})
  const toast = useToast()

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const toggleBookingArchived = () => {
    const req = booking?.archivedAt
      ? apiBookings.unarchive({ venueSlug, id })
      : apiBookings.archive({ venueSlug, id })
    req.then(refetch)
  }

  const deletePayment = () => {
    if (!deletingPayment) return

    const req = apiPayments.destroy({
      venueSlug,
      id: deletingPayment,
      payableId: booking.id,
      payableType: "booking",
    })
    req.then(() => {
      setDeletingPayment(null)
      toast("Payment Removed", "success")
      refetch()
    })
  }

  const handleSaveBookingItems = (changes: any[]) => {
    const bookingItemsAttributes = changes.map((change) => {
      const attributes: any = {
        lineItemId: change.lineItemId,
        name: change.name,
        categoryName: change.categoryName,
        quantity: change.quantity,
        amountCents: change.amountCents,
      }

      if (change.id) {
        attributes.id = change.id
      }

      if (change.action === "remove") {
        attributes._destroy = true
      }

      return attributes
    })

    const data = {
      venue_slug: venueSlug,
      id: booking.id,
      booking: {
        bookingItemsAttributes: bookingItemsAttributes,
      },
    }

    apiBookings
      .update({ data })
      .then(() => {
        toast(`Successfully updated ${changes.length} booking items`, "success")
        refetch()
      })
      .catch((res) => {
        toast(res.body?.errors?.join(", ") || "Failed to update booking items", "danger")
      })
  }

  const handleRemoveBookingItem = (bookingItemId: string) => {
    const data = {
      venue_slug: venueSlug,
      id: booking.id,
      booking: {
        bookingItemsAttributes: [
          {
            id: bookingItemId,
            _destroy: true,
          },
        ],
      },
    }

    apiBookings
      .update({ data })
      .then(() => {
        toast("Booking item removed", "success")
        refetch()
      })
      .catch((res) => {
        toast(res.body?.errors?.join(", ") || "Failed to remove booking item", "danger")
      })
  }

  const sendInvoice = () => {
    if (!confirmingInvoice) return

    // Start the loading state
    setSendingInvoice(confirmingInvoice)
    // Close the confirmation modal
    setConfirmingInvoice(null)

    toast("Invoice sending in progress...", "info")

    const req = apiPayments.sendInvoice({
      venueSlug,
      id: confirmingInvoice,
      payableId: booking.id,
      payableType: "booking",
    })
    req
      .then(() => {
        setSendingInvoice(null)
        toast("Invoice sent successfully", "success")
        refetch()
      })
      .catch((res) => {
        setSendingInvoice(null)
        toast(res.body?.errors?.join(", ") || "Failed to send invoice", "danger")
      })
  }

  const handleStatusChangeToConfirmed = () => {
    const data = {
      venue_slug: venueSlug,
      id: booking.id,
      booking: {
        status: "pending",
      },
    }

    apiBookings
      .update({ data })
      .then(() => {
        toast("Booking status updated.", "success")
        refetch()
      })
      .catch((res) => {
        toast(res.body.errors.join(", "), "danger")
      })
      .finally(() => {
        setShowStatusChangeModal(false)
      })
  }

  const formatStatus = (status: string) => {
    if (status === "pending") {
      return <span className="badge badge-lg bg-warning">Pending</span>
    } else if (status === "confirmed") {
      return <span className="badge badge-lg bg-success">Confirmed</span>
    } else {
      return <span className="badge badge-lg bg-secondary">{status}</span>
    }
  }

  const formatPaymentStatus = (status: string, isOverdue: boolean) => {
    if (status === "paid") {
      return <span className="badge badge-lg bg-success">Paid</span>
    } else if (status === "pending") {
      return (
        <span className={`badge badge-lg ${isOverdue ? "bg-danger" : "bg-warning"}`}>
          {isOverdue ? "Overdue" : "Pending"}
        </span>
      )
    } else {
      return <span className="badge badge-lg bg-secondary">Draft</span>
    }
  }

  const canSendInvoice = (payment: Payment) => {
    return payment.type === "Invoice" && payment.status === "draft"
  }

  const renderPaymentActions = useCallback(
    (payment: Payment) => {
      const isSending = sendingInvoice === payment.id
      return (
        <div className="d-flex gap-1">
          {isSending ? (
            <Loader2 className="text-primary spinner" size={24} />
          ) : (
            canSendInvoice(payment) && (
              <Send
                className="text-primary cursor-pointer"
                onClick={() => setConfirmingInvoice(payment.id)}
              />
            )
          )}
          <TrashIcon
            className={`text-danger ${isSending ? "opacity-50" : "cursor-pointer"}`}
            onClick={isSending ? undefined : () => setDeletingPayment(payment.id)}
          />
        </div>
      )
    },
    [sendingInvoice]
  )

  const getBackNavigation = () => {
    const navigationState = getBookingsNavigationState()

    if (
      navigationState?.path &&
      (navigationState.path.includes("/clients/") || navigationState.path.includes("/areas/"))
    ) {
      return {
        path: navigationState.path,
        title: navigationState.title,
      }
    }

    return {
      path: getBookingsBackPath(),
      title: getBookingsBackTitle(),
    }
  }

  const backNavigation = getBackNavigation()

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(backNavigation.path)}
              aria-label={`Go back to ${backNavigation.title}`}
              title={`Go back to ${backNavigation.title}`}
            >
              <ArrowLeft />
            </button>
            <span>{`${booking?.name}${booking?.archivedAt ? " (Archived)" : ""}`}</span>
          </div>
        }
      >
        <>
          <BookingStatusManager booking={booking} onUpdate={refetch} />
          <CatchDropdown
            label="Actions"
            className="ms-2"
            items={[
              {
                label: "Edit Booking",
                to: `/bookings/${booking?.id}/edit`,
              },
              ...(booking?.status === "confirmed"
                ? [
                    {
                      label: "Change Status",
                      onClick: () => setShowStatusChangeModal(true),
                    },
                  ]
                : []),
              {
                label: booking?.archivedAt ? "Unarchive" : "Archive",
                onClick: toggleBookingArchived,
              },
            ]}
          />
        </>
      </PageTitle>

      <div className="row">
        <div className="col-8">
          <CatchCard title="Booking Details">
            <div className="row">
              {renderDetail("Booking Name", booking?.name, 4)}
              {renderDetail(
                "Booked Area",
                <NavigationLink type="areas" to={`/areas/${booking?.area?.id}`}>
                  {booking?.area?.name}
                </NavigationLink>,
                4
              )}
              {renderDetail("Date", moment(booking?.date).format("MMM Do YYYY"), 4)}
              <div className="row mt-2">
                {renderDetail("Additional Details", booking?.details || <em>No Details</em>, 4)}
                {renderDetail("Current Status", formatStatus(booking?.status), 4)}
              </div>
            </div>
          </CatchCard>

          <CatchCard title="Client Details">
            <div className="row">
              {renderDetail(
                "Name",
                <NavigationLink type="clients" to={`/clients/${booking?.client?.id}`}>
                  {booking?.client?.name}
                </NavigationLink>,
                4
              )}
              {renderDetail("Phone", booking?.client?.phone, 4)}
              {renderDetail("Email", booking?.client?.email, 4)}
            </div>
          </CatchCard>

          <CatchCard>
            <div className="d-flex align-items-middle mb-2">
              <h4>Selected Packs</h4>
            </div>

            <div className="row">
              {!booking?.packs || booking.packs.length === 0 ? (
                <div className="text-center p-3 text-muted">
                  No packs selected for this booking.
                </div>
              ) : (
                <>
                  <CatchTable
                    data={booking?.packs || []}
                    emptyMessage="No packs selected for this booking"
                    columns={[
                      {
                        key: "name",
                        header: "Pack Name",
                        formatter: (name: string, data: Pack) => (
                          <NavigationLink type="packs" to={`/packs/${data.id}`}>
                            {name}
                          </NavigationLink>
                        ),
                      },
                      {
                        key: "active",
                        header: "Status",
                        formatter: (active: boolean) => (active ? "Active" : "Inactive"),
                      },
                      {
                        key: "price",
                        header: "Price",
                        formatter: (price: { cents: number; currency_iso: string }) =>
                          formatCurrency(price || { cents: 0, currency_iso: "USD" }),
                      },
                      {
                        key: "items",
                        header: "Items Included",
                        formatter: (items: PackItem[]) =>
                          items?.length > 0 ? (
                            <small className="text-muted">
                              {items
                                .map(
                                  (item: PackItem) => `${item.lineItem?.name} (${item.quantity})`
                                )
                                .join(", ")}
                            </small>
                          ) : (
                            <small className="text-muted">No items</small>
                          ),
                      },
                    ]}
                    footerData={{
                      className: "table table-centered",
                      rowData: [
                        { cellData: [{ content: "" }] },
                        {
                          className: "bg-block",
                          cellData: [
                            { content: "Total", colSpan: 2 },
                            {
                              content: formatCurrency(
                                booking?.packsTotal || { cents: 0, currency_iso: "USD" }
                              ),
                              colSpan: 2,
                            },
                          ],
                        },
                      ],
                    }}
                  />
                </>
              )}
            </div>
          </CatchCard>

          <CatchCard>
            <div className="d-flex align-items-center mb-2">
              <h4>Line Items</h4>
              <button
                className="btn btn-primary btn-sm ms-auto"
                onClick={() => setIsLineItemModalOpen(true)}
              >
                <Plus className="me-2" />
                Add Line Item
              </button>
            </div>

            <div className="row">
              {!booking?.bookingItems || booking.bookingItems.length === 0 ? (
                <div className="text-center p-3 text-muted">No booking items added yet.</div>
              ) : (
                <>
                  <CatchTable
                    data={booking?.bookingItems || []}
                    emptyMessage="No Booking Items"
                    columns={[
                      {
                        key: "name",
                        header: "Title",
                        formatter: (name: string, data: BookingItem) => (
                          <NavigationLink type="line-items" to={`/line-items/${data.lineItem.id}`}>
                            {name}
                          </NavigationLink>
                        ),
                      },
                      {
                        key: "categoryName",
                        header: "Category",
                        formatter: (category: string) => category || "—",
                      },
                      {
                        key: "quantity",
                        header: "Quantity",
                        formatter: (quantity: number) => quantity,
                      },
                      {
                        key: "amount",
                        header: "Price",
                        formatter: (amount: { cents: number; currency_iso: string }) =>
                          formatCurrency(amount),
                      },
                      {
                        key: "subtotal",
                        header: "Subtotal",
                        formatter: (subtotal: { cents: number; currency_iso: string }) =>
                          formatCurrency(subtotal),
                      },
                      {
                        key: "modifierNames",
                        header: "Modifiers",
                        formatter: (modifierNames: string[]) => modifierNames.join(", "),
                      },
                      {
                        key: "actions" as any,
                        header: "",
                        formatter: (_: any, data: any) => (
                          <TrashIcon
                            className="text-danger cursor-pointer"
                            onClick={() => handleRemoveBookingItem(data.id)}
                          />
                        ),
                      },
                    ]}
                    footerData={{
                      className: "table table-centered",
                      rowData: [
                        { cellData: [{ content: "" }] },
                        {
                          className: "bg-block",
                          cellData: [
                            { content: "Total", colSpan: 4 },
                            {
                              content: formatCurrency(
                                booking?.lineItemsTotal || { cents: 0, currency_iso: "USD" }
                              ),
                              colSpan: 5,
                            },
                          ],
                        },
                      ],
                    }}
                  />
                </>
              )}
            </div>
          </CatchCard>

          <CatchCard>
            <div className="d-flex align-items-center mb-2">
              <h4 className="card-title mb-0">Payments</h4>
              <button
                className="btn btn-primary ms-auto"
                onClick={() => setIsPaymentModalOpen(true)}
              >
                <Plus className="me-2" />
                Create Invoice
              </button>
            </div>
            <CatchTable
              data={booking?.payments || []}
              emptyMessage="No Payments"
              columns={[
                {
                  key: "type",
                  header: "Type",
                },
                {
                  key: "createdBy",
                  header: "Created By",
                  formatter: (createdBy: any) => createdBy?.name || createdBy?.email,
                },
                {
                  key: "amount",
                  header: "Amount",
                  formatter: (amount) => formatCurrency(amount),
                },
                {
                  key: "sentOn",
                  header: "Sent On",
                  formatter: (date) => (date ? moment(date).format("MM/D/YYYY HH:mm") : "—"),
                },
                {
                  key: "paidOn",
                  header: "Paid On",
                  formatter: (date) => (date ? moment(date).format("MM/D/YYYY HH:mm") : "—"),
                },
                {
                  key: "status",
                  header: "Status",
                  formatter: (status, data: Payment) => formatPaymentStatus(status, data.isOverdue),
                },
                {
                  key: "id",
                  header: "Actions",
                  formatter: (_d, data: Payment) => renderPaymentActions(data),
                },
              ]}
            />
          </CatchCard>
        </div>

        <div className="col-4">
          <CatchCard title="Financials">
            <div className="row">
              {renderDetail("Anticipated Revenue", formatCurrency(booking?.anticipatedRevenue), 12)}
            </div>
          </CatchCard>

          <CatchCard title="Internal Details">
            <div className="row">
              {booking?.archivedAt &&
                renderDetail("Archived", moment(booking.archivedAt).fromNow(), 12)}
              {renderDetail("Created by", booking?.createdBy?.email, 12)}
              {renderDetail(
                "Created on",
                moment(booking?.createdAt).format("MMM Do YYYY hh:mma"),
                12
              )}
            </div>
          </CatchCard>

          <CatchCard title="Notes">
            <ResourceNotes resourceType="booking" resourceId={booking?.id} />
          </CatchCard>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!deletingPayment}
        onClose={() => setDeletingPayment(null)}
        onConfirm={deletePayment}
        title="Delete Payment?"
      />

      <ConfirmationModal
        isOpen={!!confirmingInvoice}
        onClose={() => setConfirmingInvoice(null)}
        onConfirm={sendInvoice}
        title="Send Invoice?"
        description="Are you sure you want to send this invoice? This will email the invoice to the client."
      />

      {isPaymentModalOpen && (
        <PaymentModal
          onClose={() => setIsPaymentModalOpen(false)}
          onSuccess={refetch}
          bookingId={booking?.id}
          booking={booking}
        />
      )}

      {isLineItemModalOpen && (
        <LineItemModal
          isOpen={isLineItemModalOpen}
          onClose={() => setIsLineItemModalOpen(false)}
          onSave={handleSaveBookingItems}
          existingBookingItems={booking?.bookingItems || []}
        />
      )}

      <CatchModal
        isOpen={showStatusChangeModal}
        onClose={() => setShowStatusChangeModal(false)}
        title="Confirm Status Change"
        footer={
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowStatusChangeModal(false)}
            >
              No
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleStatusChangeToConfirmed}
            >
              Yes
            </button>
          </div>
        }
      >
        <p>
          Are you sure you want to mark this booking as <strong>Pending</strong>?
        </p>
      </CatchModal>
    </LoadingWrapper>
  )
}

export default BookingView
